/**
 * Mapping des catégories backend vers emoji + label.
 * Backend renvoie strictement : Cuisine | Service | Cadre | Prix | Attente | Accessibilité
 * Ces strings sont des identifiants techniques (cf. backend/services/insightsService.js).
 *
 * Les labels affichés UI sont traduits via i18n (namespace `tabs`, clé `insights.category.*`).
 * Les helpers ci-dessous acceptent un `t` optionnel — fallback FR si absent (rétrocompat).
 */

const CATEGORY_META = {
  'Cuisine':       { emoji: '🍽️', fallback: 'Cuisine',       i18nKey: 'cuisine' },
  'Service':       { emoji: '🤝', fallback: 'Service',       i18nKey: 'service' },
  'Cadre':         { emoji: '🏛️', fallback: 'Cadre',         i18nKey: 'cadre' },
  'Prix':          { emoji: '💶', fallback: 'Prix',          i18nKey: 'prix' },
  'Attente':       { emoji: '⏱️', fallback: 'Attente',       i18nKey: 'attente' },
  'Accessibilité': { emoji: '♿', fallback: 'Accessibilité', i18nKey: 'accessibilite' },
};

const FALLBACK_META = { emoji: '✨', fallback: 'Autre', i18nKey: 'autre' };

export function categoryEmoji(name) {
  return CATEGORY_META[name]?.emoji || FALLBACK_META.emoji;
}

/**
 * @param {string} name  Catégorie technique (Cuisine, Service, ...)
 * @param {function} [t] Fonction i18n optionnelle (useTranslation('tabs')). Si absente → fallback FR.
 */
export function categoryLabel(name, t) {
  const meta = CATEGORY_META[name] || FALLBACK_META;
  if (typeof t === 'function') return t(`insights.category.${meta.i18nKey}`);
  return meta.fallback || name || 'Autre';
}

// Exporté pour les composants qui veulent accéder à la clé i18n (rare).
export { CATEGORY_META };

export function sentimentColor(score) {
  const s = Number(score) || 0;
  if (s >= 80) return '#2D7A4A';
  if (s >= 60) return '#B8860B';
  return '#C8442A';
}

/**
 * @param {number} score 0-100
 * @param {function} [t] Fonction i18n optionnelle. Si absente → fallback FR.
 */
export function sentimentLabel(score, t) {
  const s = Number(score) || 0;
  let key;
  if (s >= 80) key = 'sentiment_excellent';
  else if (s >= 65) key = 'sentiment_bon';
  else if (s >= 50) key = 'sentiment_a_ameliorer';
  else key = 'sentiment_critique';
  if (typeof t === 'function') return t(`insights.${key}`);
  // Fallback FR
  return { sentiment_excellent: 'Excellent', sentiment_bon: 'Bon', sentiment_a_ameliorer: 'À améliorer', sentiment_critique: 'Critique' }[key];
}

export function typeColor(type) {
  if (type === 'positive') return '#2D7A4A';
  if (type === 'alert') return '#C8442A';
  return '#6B6B6B';
}

/**
 * Badge type mobile — l'emoji reste hardcodé (visuel), le label vient de i18n.
 * @param {string} type 'positive' | 'alert' | autre
 * @param {function} [t] Fonction i18n optionnelle. Si absente → fallback FR.
 */
export function typeBadge(type, t) {
  let emoji, key, fallback, variant;
  if (type === 'positive') { emoji = '💪'; key = 'force'; fallback = 'Force'; variant = 'success'; }
  else if (type === 'alert') { emoji = '⚠️'; key = 'a_ameliorer'; fallback = 'À améliorer'; variant = 'warning'; }
  else { emoji = '→'; key = 'stable'; fallback = 'Stable'; variant = 'default'; }
  const text = (typeof t === 'function') ? t(`insights.badge.${key}`) : fallback;
  return { label: `${emoji} ${text}`, variant };
}

/**
 * Détecte si le backend a renvoyé son fallback générique
 * (cas "matière insuffisante" ou Claude refuse d'extraire).
 */
export function isInsufficientData(data) {
  if (!data) return true;
  if (data?.meta?.reason === 'insufficient_data') return true;
  const list = Array.isArray(data.insights) ? data.insights : [];
  if (list.length === 0) return true;
  // Vague 10 commit 2/3 — Bug 5 :
  // L'heuristique double-check sur les titres "Bien démarré" / "Collectez vos premiers avis"
  // (Session 4) classait les insights pédagogiques du backend comme "insuffisants" et
  // affichait un EmptyState à la place. Or ces 2 insights neutral SONT utiles à l'utilisateur
  // (titre + description + action concrète) — on les affiche maintenant comme des cards.
  // Le fallback EmptyState ne se déclenche plus que sur :
  //   - data null / pas d'insights array
  //   - liste vide (length === 0)
  //   - meta.reason === 'insufficient_data' (backend explicite, < 5 avis textuels)
  return false;
}

/**
 * Détermine le type dominant d'un groupe d'insights.
 * Priorité : alert (un seul suffit à dominer) > positive > neutral.
 */
function dominantTypeOf(insights) {
  let pos = 0, neu = 0, ale = 0;
  for (const i of insights) {
    if (i.type === 'positive') pos++;
    else if (i.type === 'alert') ale++;
    else neu++;
  }
  if (ale > 0) return 'alert';
  if (pos > neu) return 'positive';
  return 'neutral';
}

/**
 * Score d'une catégorie (0-100).
 * (positive_count - alert_count + total) / total * 100, clampé 0-100.
 */
function categoryScore(insights) {
  const total = insights.length;
  if (total === 0) return 50;
  let pos = 0, ale = 0;
  for (const i of insights) {
    if (i.type === 'positive') pos++;
    else if (i.type === 'alert') ale++;
  }
  const raw = ((pos - ale + total) / total) * 100;
  return Math.max(0, Math.min(100, Math.round(raw / 2 + 25)));
}

/**
 * Regroupe les insights par catégorie.
 * Retourne un Array (ordre d'apparition) plutôt qu'une Map pour faciliter le rendu React.
 */
export function groupByCategory(insights) {
  const map = new Map();
  for (const i of (insights || [])) {
    const cat = i.category || 'Autre';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat).push(i);
  }
  return Array.from(map.entries()).map(([category, list]) => ({
    category,
    insights: list,
    totalVerbatims: list.reduce((acc, x) => acc + (Array.isArray(x.verbatims) ? x.verbatims.length : 0), 0),
    dominantType: dominantTypeOf(list),
    score: categoryScore(list),
  }));
}

/**
 * Top n catégories triées par priorité :
 *   1. Présence d'au moins une `alert` → priorité haute
 *   2. Plus grand nombre d'insights
 *   3. Score le plus bas en cas d'égalité
 */
export function topCategories(insights, n = 3) {
  const groups = groupByCategory(insights);
  groups.sort((a, b) => {
    const aHasAlert = a.insights.some(i => i.type === 'alert');
    const bHasAlert = b.insights.some(i => i.type === 'alert');
    if (aHasAlert !== bHasAlert) return aHasAlert ? -1 : 1;
    if (b.insights.length !== a.insights.length) return b.insights.length - a.insights.length;
    return a.score - b.score;
  });
  return groups.slice(0, n);
}

/**
 * Top n actions prioritaires : insights de type 'alert' en priorité,
 * fallback sur insights avec une `action` non vide si pas assez d'alertes.
 */
export function topActions(insights, n = 3) {
  const list = Array.isArray(insights) ? insights : [];
  const alerts = list.filter(i => i.type === 'alert' && i.action);
  if (alerts.length >= n) return alerts.slice(0, n);
  const others = list.filter(i => i.type !== 'alert' && i.action);
  return [...alerts, ...others].slice(0, n);
}

/**
 * Compte les types pour le breakdown du SentimentScore.
 */
export function countByType(insights) {
  const c = { positive: 0, neutral: 0, alert: 0 };
  for (const i of (insights || [])) {
    if (i.type === 'positive') c.positive++;
    else if (i.type === 'alert') c.alert++;
    else c.neutral++;
  }
  return c;
}
